<script setup lang="ts">
import { ImagePlus, KeyRound, LogIn, LogOut, RotateCcw, Settings2, X } from '@lucide/vue'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AdaptiveOverlay from '@/components/ui/AdaptiveOverlay.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import UserAvatar from '@/components/ui/UserAvatar.vue'
import { useOverlayHistory } from '@/composables/useOverlayHistory'
import { useToast } from '@/composables/useToast'
import { api } from '@/services/api'
import { useAppStore } from '@/stores/app'
import { useLibraryStore } from '@/stores/library'
import { prepareAvatar } from '@/utils/avatar'

const route = useRoute()
const router = useRouter()
const appStore = useAppStore()
const library = useLibraryStore()
const toast = useToast()
const profileOpen = ref(false)
const passwordOpen = ref(false)
const profileName = ref('')
const profileAvatar = ref('')
const profileAvatarChanged = ref(false)
const currentPassword = ref('')
const newPassword = ref('')
const confirmNewPassword = ref('')
const avatarInput = ref<HTMLInputElement>()
const busy = ref(false)
const logoutConfirmOpen = ref(false)
const watchedHours = computed(() => Math.round(library.history.reduce((sum, item) => sum + item.position, 0) / 3600 * 10) / 10)
const recentlyVerifiedWithPasskey = computed(() => appStore.authMethod === 'passkey' && (appStore.authenticatedAt || 0) >= Date.now() / 1000 - 10 * 60)

useOverlayHistory(profileOpen, 'profile-editor')
useOverlayHistory(passwordOpen, 'password-editor')
useOverlayHistory(logoutConfirmOpen, 'logout-confirm')

onMounted(() => {
    if (route.query.login === '1') { return }
    if (appStore.initialized && !appStore.authenticated) { return }
    void appStore.initialize()
})
watch(() => appStore.user, (user) => {
    if (user && !profileOpen.value) { profileName.value = user.displayName; profileAvatar.value = user.avatar || '' }
}, { immediate: true })

function openProfile() {
    profileName.value = appStore.user?.displayName || ''
    profileAvatar.value = appStore.user?.avatar || ''
    profileAvatarChanged.value = false
    profileOpen.value = true
}

function chooseAvatar() { avatarInput.value?.click() }

async function onAvatarChange(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0]
    if (!file) { return }
    try {
        profileAvatar.value = await prepareAvatar(file)
        profileAvatarChanged.value = true
        toast.show('头像已选择，请点击保存资料', 'success')
    }
    catch (error) { toast.show(error instanceof Error ? error.message : '图片处理失败，请换一张图片', 'error') }
    finally { if (event.target instanceof HTMLInputElement) { event.target.value = '' } }
}

function removeAvatar() {
    profileAvatar.value = ''
    profileAvatarChanged.value = true
}

async function saveProfile() {
    if (!appStore.user) { return }
    const name = profileName.value.trim()
    if (!name) { toast.show('请填写显示名称', 'error'); return }
    busy.value = true
    try {
        const result = await api.updateProfile({ displayName: name, avatar: profileAvatar.value })
        if (profileAvatarChanged.value && profileAvatar.value && !result.user.avatar) { throw new Error('头像未能保存，请稍后重试') }
        appStore.applyProfile(result.user)
        profileAvatarChanged.value = false
        profileOpen.value = false
        toast.show('个人资料已更新', 'success')
    }
    catch (error) { toast.show(error instanceof Error ? error.message : '个人资料更新失败', 'error') }
    finally { busy.value = false }
}

function openPassword() {
    currentPassword.value = ''
    newPassword.value = ''
    confirmNewPassword.value = ''
    passwordOpen.value = true
}

async function savePassword() {
    if (newPassword.value !== confirmNewPassword.value) { toast.show('两次输入的新密码不一致', 'error'); return }
    busy.value = true
    try {
        await api.changePassword(currentPassword.value, newPassword.value)
        passwordOpen.value = false
        toast.show('账号密码已更新，其他登录会话已退出；已有通行密钥仍可使用', 'success')
    }
    catch (error) { toast.show(error instanceof Error ? error.message : '无法修改账号密码', 'error') }
    finally { busy.value = false }
}

async function logout() {
    busy.value = true
    try {
        await api.logout()
        library.stopCloudSync()
        library.setIdentity()
        appStore.clearAuth()
        toast.show('已安全退出', 'success')
        await router.replace('/login')
    }
    catch (error) { toast.show(error instanceof Error ? error.message : '退出登录失败', 'error') }
    finally { busy.value = false }
}

function requestLogout() { logoutConfirmOpen.value = true }
</script>

<template>
    <div class="page-shell max-w-5xl">
        <header class="border-b border-[var(--c-border)] pb-8">
            <p class="mb-3 text-xs text-[var(--accent)] font-700 tracking-[.12em] uppercase">
                账号与数据
            </p>
            <h1 class="page-title">
                我的
            </h1>
        </header>

        <section class="grid gap-8 border-b border-[var(--c-border)] py-9 md:grid-cols-[1fr_auto] md:items-center">
            <div class="min-w-0 flex items-center gap-5">
                <button class="relative grid size-18 shrink-0 place-items-center overflow-hidden border border-[var(--c-border)] rounded-full bg-[var(--c-fill-subtle)] text-[var(--c-text-soft)] transition hover:border-[var(--accent)] focus-ring" aria-label="编辑头像" :disabled="!appStore.authenticated" @click="openProfile">
                    <UserAvatar :name="appStore.user?.displayName || '用户'" :src="appStore.user?.avatar" class="size-full text-2xl" />
                    <span v-if="appStore.authenticated" class="absolute inset-x-0 bottom-0 flex justify-center bg-black/42 py-1 text-white"><ImagePlus class="size-3.5" /></span>
                </button>
                <div class="min-w-0">
                    <h2 class="truncate text-2xl font-editorial">
                        {{ appStore.user?.displayName || '用户' }}
                    </h2><p class="mt-1 text-sm text-[var(--c-text-muted)]">
                        <template v-if="appStore.user">
                            @{{ appStore.user.username }} · {{ appStore.isOwner ? '管理员' : '用户' }}
                        </template><template v-else>
                            正在读取账号信息
                        </template>
                    </p>
                </div>
            </div>
            <div class="flex flex-wrap gap-3 md:justify-end">
                <RouterLink v-if="!appStore.authenticated" :to="{ path: '/me', query: { login: '1', redirect: '/me' } }" class="btn-primary">
                    <LogIn class="size-4" />{{ appStore.setupRequired ? '初始化账号' : '登录' }}
                </RouterLink>
                <template v-else>
                    <button class="btn-ghost" @click="openPassword">
                        <KeyRound class="size-4" />修改密码
                    </button><button class="btn-ghost" :disabled="busy" @click="requestLogout">
                        <LogOut class="size-4" />退出登录
                    </button>
                </template>
            </div>
        </section>

        <section class="grid grid-cols-3 border-b border-[var(--c-border)] py-8">
            <div class="border-r border-[var(--c-border)] px-3 text-center first:pl-0 sm:text-left">
                <p class="text-3xl font-editorial">
                    {{ library.favorites.length }}
                </p><p class="mt-1 text-xs text-[var(--c-text-muted)]">
                    收藏
                </p>
            </div>
            <div class="border-r border-[var(--c-border)] px-3 text-center sm:text-left">
                <p class="text-3xl font-editorial">
                    {{ library.history.length }}
                </p><p class="mt-1 text-xs text-[var(--c-text-muted)]">
                    看过
                </p>
            </div>
            <div class="px-3 text-center sm:text-left">
                <p class="text-3xl font-editorial">
                    {{ watchedHours }}
                </p><p class="mt-1 text-xs text-[var(--c-text-muted)]">
                    小时
                </p>
            </div>
        </section>

        <section v-if="appStore.isOwner" class="border-b border-[var(--c-border)] py-8">
            <RouterLink to="/admin" class="group flex items-center gap-4 border border-[var(--c-border)] rounded-2xl bg-[var(--c-fill-subtle)] p-5 transition hover:border-[var(--accent)] focus-ring">
                <span class="grid size-11 place-items-center rounded-full bg-[rgb(var(--accent-rgb)/10%)] text-[var(--accent)]"><Settings2 class="size-5" /></span><span><strong class="block text-base font-650">账号管理</strong><small class="mt-1 block text-sm text-[var(--c-text-muted)]">通行密钥、用户与邀请码</small></span>
            </RouterLink>
        </section>
    </div>

    <AdaptiveOverlay :open="profileOpen" label="编辑个人资料" max-width="28rem" mode="dialog" @close="profileOpen = false">
        <form class="p-6 sm:p-8" @submit.prevent="saveProfile">
            <div class="flex items-start justify-between gap-4">
                <div>
                    <p class="text-sm text-[var(--accent)] font-700">
                        个人资料
                    </p><h2 class="mt-1 text-2xl font-editorial">
                        编辑资料
                    </h2>
                </div>
                <button type="button" class="grid size-11 place-items-center rounded-full text-[var(--c-text-muted)] hover:bg-[var(--c-fill-hover)] focus-ring" aria-label="关闭" @click="profileOpen = false">
                    <X class="size-5" />
                </button>
            </div>

            <div class="mt-6 flex flex-col items-center gap-3">
                <button type="button" class="relative grid size-24 place-items-center overflow-hidden border border-[var(--c-border)] rounded-full bg-[var(--c-fill-subtle)] text-[var(--c-text-soft)] focus-ring" aria-label="选择新头像" @click="chooseAvatar">
                    <UserAvatar :name="profileName || appStore.user?.displayName || '用户'" :src="profileAvatar" class="size-full text-3xl" /><span class="absolute inset-x-0 bottom-0 flex justify-center bg-black/45 py-1.5 text-white"><ImagePlus class="size-4" /></span>
                </button>
                <input ref="avatarInput" type="file" accept="image/*" class="hidden" @change="onAvatarChange">
                <button v-if="profileAvatar" type="button" class="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-[var(--c-text-muted)] transition hover:bg-[var(--c-fill-hover)] hover:text-[var(--c-text)] focus-ring" @click="removeAvatar">
                    <RotateCcw class="size-3.5" />恢复默认头像
                </button>
            </div>
            <label class="auth-field mt-6 block">显示名称
                <input v-model="profileName" class="auth-input" maxlength="30" autocomplete="nickname">
            </label>

            <button class="mt-6 btn-primary h-12 w-full" :disabled="busy">
                {{ busy ? '正在保存' : '保存资料' }}
            </button>
        </form>
    </AdaptiveOverlay>

    <AdaptiveOverlay :open="passwordOpen" label="修改账号密码" max-width="28rem" mode="dialog" @close="passwordOpen = false">
        <form class="p-6 sm:p-8" @submit.prevent="savePassword">
            <div class="flex items-start justify-between gap-4">
                <div>
                    <p class="text-sm text-[var(--accent)] font-700">
                        账户安全
                    </p><h2 class="mt-1 text-2xl font-editorial">
                        修改账号密码
                    </h2>
                </div><button type="button" class="grid size-11 place-items-center rounded-full text-[var(--c-text-muted)] hover:bg-[var(--c-fill-hover)] focus-ring" aria-label="关闭" @click="passwordOpen = false">
                    <X class="size-5" />
                </button>
            </div>
            <p class="mt-3 text-sm text-[var(--c-text-muted)] leading-6">
                {{ recentlyVerifiedWithPasskey ? '你刚刚通过通行密钥验证，可以直接设置新密码。' : '请输入当前密码以确认身份。' }} 已有通行密钥不会受到影响。
            </p>
            <label v-if="!recentlyVerifiedWithPasskey" class="auth-field mt-5 block">当前账号密码<input v-model="currentPassword" required type="password" autocomplete="current-password" class="auth-input" placeholder="请输入当前账号密码"></label><label class="auth-field mt-4 block">新账号密码<input v-model="newPassword" required type="password" minlength="8" autocomplete="new-password" class="auth-input" placeholder="至少 8 个字符"></label><label class="auth-field mt-4 block">确认新密码<input v-model="confirmNewPassword" required type="password" minlength="8" autocomplete="new-password" class="auth-input" placeholder="再次输入"></label><button class="mt-6 btn-primary h-12 w-full" :disabled="busy">
                {{ busy ? '正在更新' : '保存新密码' }}
            </button>
        </form>
    </AdaptiveOverlay>
    <ConfirmDialog :open="logoutConfirmOpen" title="退出当前账号？" description="退出后需要重新登录才能访问 CineFlow。云端收藏和观看进度不会被删除。" eyebrow="退出登录" confirm-label="确认退出" @close="logoutConfirmOpen = false" @confirm="logoutConfirmOpen = false; void logout()" />
</template>

<style scoped>
.auth-field {
    color: var(--c-text-soft);
    font-size: 0.8125rem;
    font-weight: 650;
}

.auth-input {
    width: 100%;
    height: 3rem;
    margin-top: 0.5rem;
    padding: 0 1rem;
    border: 1px solid var(--c-border-strong);
    border-radius: 1rem;
    outline: none;
    color: var(--c-text);
    background: var(--c-fill-subtle);
    font-size: 0.9375rem;
}

.auth-input:focus {
    border-color: rgb(var(--accent-rgb) / 65%);
    background: var(--c-surface-raised);
}
</style>
